import { Component, computed, inject, input, output } from '@angular/core';
import { ContactTypeEnum } from '../../../../../../../../types/src/common/enum/contact-type.enum';
import { OnlineStatusEnum } from '../../../../../../../../types/src/common/enum/online-status.enum';
import { PersonContactTypeEnum } from '../../../../../../../../types/src/services/enum/person-contact-type.enum';
import { ContactCardStore } from './contact-card.store';
import { MenuItem } from '@openng/optimus-ui/api';
import { isAllowed, translate } from '../../../../shared/shared.global-service';
import { UserWorkplaceIsOpenPipe } from '../../user-management/user-workplace-table/user-workplace-table.pipes';
import {
    WorkingHoursDialogComponent,
} from '../../../../../components/user-management/user-detail/working-hours-dialog/working-hours-dialog.component';
import {
    PersonContactUpsertDialogComponent,
} from '../person-contact-upsert-dialog/person-contact-upsert-dialog.component';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../content.error';
import {
    PersonAuthorizationDialogData,
    PersonAuthorizationsDialogComponent,
} from '../person-authorizations-dialog/person-authorizations-dialog.component';
import {
    PersonRestrictionDialogData,
    PersonRestrictionsDialogComponent,
} from '../person-restrictions-dialog/person-restrictions-dialog.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { Card } from '@openng/optimus-ui/card';
import { Draggable, Droppable } from '@openng/optimus-ui/dragdrop';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { Tag } from '@openng/optimus-ui/tag';
import { Menu } from '@openng/optimus-ui/menu';
import { Divider } from '@openng/optimus-ui/divider';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { PersonContactGridItemObject } from '@it2go/types';
import { AvatarComponent } from '../../../../shared/components/kit/avatar/avatar.component';
import { PersonInitialsPipe } from '../../../../shared/pipes/personInitials';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import { FormatAddressPipe } from '../../../../shared/pipes/format-address.pipe';
import { ShortDatePipe } from '../../../../shared/pipes/short-date.pipe';
import { MapLinkComponent } from '../../../../shared/components/map-link/map-link.component';

@Component({
    selector: 'app-contact-card',
    templateUrl: 'contact-card.component.html',
    styleUrls: ['contact-card.component.scss'],
    providers: [ContactCardStore],
    imports: [
        Card,
        Draggable,
        Droppable,
        Tooltip,
        TranslatePipe,
        NgClass,
        Tag,
        Menu,
        Divider,
        ButtonComponent,
        AvatarComponent,
        PersonInitialsPipe,
        FormatPersonPipe,
        FormatAddressPipe,
        ShortDatePipe,
        MapLinkComponent,

    ],
})
export class ContactCardComponent {
    protected readonly store = inject(ContactCardStore);


    public readonly dialogService = inject(BaseDialogService);
    public readonly confirm = inject(ConfirmBuilder);

    public contact = input.required<PersonContactGridItemObject>();
    public folderId = input.required<number>();

    public dragStart = output<PersonContactGridItemObject>();
    public dragEnd = output<number>();

    protected isActive = computed(() => this.contact().active);
    protected isContact = computed(() => this.contact().subType === ContactTypeEnum.contact);
    protected isAuthorized = computed(() => this.contact().subType === ContactTypeEnum.authorized);
    protected isDoctor = computed(() => this.contact().subType === ContactTypeEnum.doctor);
    protected isPrimary = computed(() => this.contact().primary);
    protected contactRelation = computed(() => {
        if (this.isContact()) {
            return this.contact().relation;
        } else if (this.isAuthorized()) {
            return this.contact().data?.purposeOfAuthorization;
        } else {
            return '';
        }
    });
    protected severity = computed(() => {
        if (!this.isActive()) {
            return 'secondary';
        } else if (this.isContact()) {
            return 'info';
        } else if (this.isAuthorized()) {
            return 'warn';
        } else if (this.isDoctor()) {
            return 'help';
        } else if (this.contact().type === PersonContactTypeEnum.AUTHORIZATION) {
            return 'danger';
        } else if (this.contact().type === PersonContactTypeEnum.RESTRICTION) {
            return 'success';
        }

        return null;
    });
    protected textClass = computed(() => `text-${this.severity()}`);

    protected menuItems = computed(() => {
        const items: MenuItem[] = [];
        if (!this.isDoctor() && this.isActive() && isAllowed['folderContact.update']()) {
            items.push(
                this.isPrimary()
                    ? {
                        icon: 'pi pi-star',
                        label: translate.instant('database.persons.contacts.settings.unmarkPrimary'),
                        command: () => this.unmarkPrimary(),
                    }
                    : {
                        icon: 'pi pi-star-fill',
                        label: translate.instant('database.persons.contacts.settings.markAsPrimary'),
                        command: () => this.markAsPrimary(),
                    },
            );
        }

        if (isAllowed['folderContact.update']()) {
            items.push({
                icon: 'pi pi-pencil',
                label: translate.instant('database.persons.contacts.settings.edit'),
                command: () => this.editContact(),
            });

            if (this.contact().type === PersonContactTypeEnum.CONTACT || this.isActive()) {
                items.push(
                    this.isActive()
                        ? {
                            icon: 'pi pi-ban',
                            label: translate.instant('database.persons.contacts.settings.deactivate'),
                            command: () => this.deactivateContact(),
                        }
                        : {
                            icon: 'pi pi-refresh',
                            label: translate.instant('database.persons.contacts.settings.activate'),
                            command: () => this.activateContact(),
                        },
                );
            }
        }

        if (isAllowed['folderContact.delete']()) {
            items.push({
                icon: 'pi pi-trash',
                label: translate.instant('database.persons.contacts.settings.delete'),
                command: (event: { originalEvent?: Event }) => this.deleteContact(event.originalEvent),
            });
        }

        return items;
    });

    protected opened = computed(() => {
        const workplace = this.contact().doctorWorkplace;

        if (workplace) {
            const tmp = new UserWorkplaceIsOpenPipe();
            return tmp.transform(workplace.hours || [], workplace.validFrom);
        }

        return false;
    });

    async markAsPrimary() {
        if (!isAllowed['folderContact.update']()) return;
        await this.store.api.markPersonContactAsPrimary({
            folderId: this.folderId(),
            contactId: this.contact().id,
        });
        contentGlobalSuccess.next('common.updated');
    }

    async unmarkPrimary() {
        if (!isAllowed['folderContact.update']()) return;
        await this.store.api.unmarkPrimaryPersonContact({
            folderId: this.folderId(),
        });
        contentGlobalSuccess.next('common.updated');
    }

    editContact() {
        if (!isAllowed['folderContact.update']()) return;
        if (this.contact().type === PersonContactTypeEnum.CONTACT) {
            this.dialogService.open(
                PersonContactUpsertDialogComponent,
                {
                    header: translate.instant('database.persons.contacts.editHeader'),
                    styleClass: 'p-dialog-width-60',
                    data: {
                        contact: this.contact(),
                    },
                },
            );
        } else if (this.contact().type === PersonContactTypeEnum.AUTHORIZATION) {
            const data: PersonAuthorizationDialogData = {
                authorization: this.contact(),
                personId: this.contact().personId,
            };

            this.dialogService.open(PersonAuthorizationsDialogComponent, {
                maximizable: true,
                data: data,
                baseZIndex: 2000,
                focusOnShow: false,
            });
        } else if (this.contact().type === PersonContactTypeEnum.RESTRICTION) {
            const data: PersonRestrictionDialogData = {
                restriction: this.contact(),
                personId: this.contact().personId,
            };

            this.dialogService.open(PersonRestrictionsDialogComponent, {
                maximizable: true,
                data: data,
                baseZIndex: 2000,
                focusOnShow: false,
            });
        }
    }

    async activateContact() {
        if (!isAllowed['folderContact.update']()) return;
        if (this.contact().type === PersonContactTypeEnum.CONTACT) {
            await this.store.api.updatePersonContact({
                id: this.contact().id,
                active: true,
            });
            contentGlobalSuccess.next('common.updated');
        }
    }

    async deactivateContact() {
        if (!isAllowed['folderContact.update']()) return;
        if (this.contact().type === PersonContactTypeEnum.CONTACT) {
            await this.store.api.updatePersonContact({
                id: this.contact().id,
                active: false,
            });
        } else {
            await this.store.api.updatePersonContact({
                id: this.contact().id,
                validTo: new Date(new Date().setHours(0, 0, 0, 0) - 1).toISOString(),
            });
        }
        contentGlobalSuccess.next('common.updated');
    }

    deleteContact(event?: Event) {
        if (!isAllowed['folderContact.delete']()) return;
        this.confirm.confirm(
            'database.persons.contacts.settings.deleteConfirmationHeader',
            'database.persons.contacts.settings.deleteConfirmationLabel',
            async () => {
                await this.store.api.deletePersonContact({
                    id: this.contact().id,
                });
                contentGlobalSuccess.next('common.deleted');
            },
            null,
            'pi-trash',
            event?.target ?? undefined,
        );
    }

    sendEmail() {
        window.location.href = `mailto:${this.contact().user.email}`;
    }

    callPhone(phoneNumber: string | null) {
        if (phoneNumber) {
            window.location.href = `tel:${phoneNumber}`;
        }
    }

    protected showWorkingHours(): void {
        this.dialogService.open(
            WorkingHoursDialogComponent,
            {
                header: translate.instant('folderContact.doctorHoursTlp'),
                styleClass: 'p-dialog-width-30',
                data: {
                    hours: this.contact().doctorWorkplace?.hours,
                },
            },
        );
    }

    protected readonly OnlineStatusEnum = OnlineStatusEnum;
    protected readonly isAllowed = isAllowed;
    protected readonly PersonContactTypeEnum = PersonContactTypeEnum;
}
